import { IconEdit, IconTrash } from '@tabler/icons-react';
import { AppTable, AppTableProps, RemoveModal } from 'components/organisms';
import { appPaths } from 'consts/paths/paths';
import { commonTexts, settingsTexts } from 'consts/text';
import { generatePath } from 'react-router-dom';
import { MealTypeDTO, MealTypeId } from 'types/mealTypes/mealTypes';
import { mealTypeFields } from 'consts/mealTypes/mealTypeFields';
import { useMemo, useState } from 'react';
import { useSettingsAbilities } from 'abilities';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { useMealTypesPageNetworkManage } from './hooks/useMealTypesPageNetworkManage';

const columns: AppTableProps<MealTypeDTO>['columns'] = [
    {
        title: settingsTexts.mealTypes.columnNames.name,
        dataIndex: mealTypeFields.name,
        key: mealTypeFields.name,
        isVisibleAsExtendableInMobile: false,
    },
];

export const MealTypesPage = () => {
    const [selectedToRemoveMealTypeId, setSelectedToRemoveMealTypeId] = useState<MealTypeId | null>(
        null,
    );
    const { statefulNavigate } = useNavigateWithState();
    const { settingsAbilities } = useSettingsAbilities();
    const { checkIfCanManageDietSettings } = settingsAbilities;
    const canManageMealTypeSettings = checkIfCanManageDietSettings();

    const handleRemoveMealTypeClick = (mealType: MealTypeDTO) =>
        setSelectedToRemoveMealTypeId(mealType.id);

    const onMealTypeRemoveRequestFinish = () => setSelectedToRemoveMealTypeId(null);

    const getOptions = useMemo(() => {
        if (!canManageMealTypeSettings) {
            return undefined;
        }

        return [
            {
                label: commonTexts.actionLabels.edit,
                onClick: (mealType: MealTypeDTO) =>
                    statefulNavigate(
                        generatePath(appPaths.app.settings.diets.mealTypes.edit.pickedMealType, {
                            mealTypeId: mealType.id,
                        }),
                    ),
                Icon: <IconEdit />,
            },
            {
                label: commonTexts.actionLabels.delete,
                onClick: handleRemoveMealTypeClick,
                Icon: <IconTrash />,
            },
        ];
    }, [canManageMealTypeSettings, statefulNavigate]);

    const {
        mealTypesData,
        isMealTypesDataInitialLoading,
        isMealTypesDataRefetching,
        isMealTypesDataError,
        removeMealType,
        isRemoveMealTypeLoading,
    } = useMealTypesPageNetworkManage({
        onMealTypeRemoveRequestFinish,
    });

    const handleRemoveMealTypeConfirm = () => {
        if (!selectedToRemoveMealTypeId) {
            return;
        }
        removeMealType(selectedToRemoveMealTypeId);
    };

    const navigateToMealTypeAdd = () => statefulNavigate(appPaths.app.settings.diets.mealTypes.add);

    return (
        <>
            <RemoveModal
                open={!!selectedToRemoveMealTypeId}
                title={settingsTexts.mealTypes.removeModalTitle}
                confirmActionLoading={isRemoveMealTypeLoading}
                onOk={handleRemoveMealTypeConfirm}
                onCancel={onMealTypeRemoveRequestFinish}
            />
            <div style={{ marginTop: '16px' }}>
                <AppTable<MealTypeDTO>
                    title={settingsTexts.mealTypes.mealTypeListTitle}
                    addButtonLabel={
                        canManageMealTypeSettings ? settingsTexts.mealTypes.addMealType : undefined
                    }
                    onAddButtonClick={navigateToMealTypeAdd}
                    options={getOptions}
                    columns={columns}
                    dataSource={mealTypesData || []}
                    isError={isMealTypesDataError}
                    emptyInfo={settingsTexts.mealTypes.emptyMealTypeList}
                    isDataInitialLoading={isMealTypesDataInitialLoading}
                    isDataRefetching={isMealTypesDataRefetching}
                    isOptionsLoading={isRemoveMealTypeLoading}
                    isTableManageUtilsHidden
                    rowKey={({ id }) => id}
                />
            </div>
        </>
    );
};
